import { useSentry } from '@/hooks/useSentry';
import { SVButtonProps } from '@/types/common';
import { Button, CircularProgress, useTheme } from '@mui/material';

export default function SVButton({
  isLoading = false,
  variant = 'primary',
  breadcrumbLabel,
  ...otherProps
}: SVButtonProps) {
  const theme = useTheme();
  const { addUserActionBreadcrumb } = useSentry();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (breadcrumbLabel) {
      addUserActionBreadcrumb(`Clicked ${breadcrumbLabel} button`, 'ui.click');
    }
    otherProps.onClick?.(event);
  };
  const { startIcon, endIcon, disabled } = otherProps;

  return (
    <Button
      {...otherProps}
      onClick={handleClick}
      variant={variant}
      disableElevation
      endIcon={isLoading ? null : endIcon}
      startIcon={isLoading ? null : startIcon}
      disabled={isLoading || disabled}
      sx={{
        bgcolor: variant === 'outlined' ? 'common.white' : undefined,
        color: variant === 'outlined' ? 'primary.main' : undefined,
        '&:hover': {
          bgcolor: variant === 'outlined' ? 'primary.main' : undefined,
          color: variant === 'outlined' ? 'common.white' : undefined,
        },
        '&:not(:hover)': {
          bgcolor: variant === 'outlined' ? 'common.white' : undefined,
          color: variant === 'outlined' ? 'primary.main' : undefined,
        },
        fontWeight: 'bold',
        ...otherProps.sx,
      }}>
      {isLoading ? (
        <CircularProgress
          variant="indeterminate"
          aria-label="Loading..."
          disableShrink
          color="inherit"
          sx={{
            color:
              variant === 'outlined'
                ? theme.palette.primary.main
                : theme.palette.common.white,
            '.MuiButton-root:hover &': {
              color: theme.palette.common.white,
            },
          }}
          size={28}
        />
      ) : (
        otherProps.children
      )}
    </Button>
  );
}
